/**
 * useCycleTracking — Suivi confidentiel du cycle menstruel.
 *
 * Données strictement privées (RLS deny-by-default sur cycle_tracking).
 * Aucune propagation automatique vers les coachs / professionnels.
 * Le partage est opt-in explicite via `share_with_coach`.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type CyclePhase = "menstrual" | "follicular" | "ovulatory" | "luteal";

export type CycleData = {
  cycle_length_days: number;
  period_length_days: number;
  last_period_start: string | null;
  tracking_enabled: boolean;
  show_in_dashboard: boolean;
  share_with_coach: boolean;
};

export type PhaseInfo = {
  phase: CyclePhase;
  day: number;
  daysRemaining: number;
  label: string;
  emoji: string;
  trainingAdaptation: string;
  color: string;
};

const DEFAULT_CYCLE: CycleData = {
  cycle_length_days: 28,
  period_length_days: 5,
  last_period_start: null,
  tracking_enabled: true,
  show_in_dashboard: true,
  share_with_coach: false,
};

/**
 * Calcule la phase actuelle du cycle à partir de la date de début des dernières règles.
 * Pure function — testable sans DB.
 */
export function computeCurrentPhase(
  lastPeriodStart: string,
  cycleLength: number,
  periodLength: number,
): PhaseInfo {
  const start = new Date(lastPeriodStart + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const daysSinceStart = Math.floor(
    (today.getTime() - start.getTime()) / 86400000,
  );
  const dayInCycle = (((daysSinceStart % cycleLength) + cycleLength) % cycleLength) + 1;

  // Phase 1 — Menstruelle
  if (dayInCycle <= periodLength) {
    return {
      phase: "menstrual",
      day: dayInCycle,
      daysRemaining: periodLength - dayInCycle + 1,
      label: "Phase menstruelle",
      emoji: "🌑",
      trainingAdaptation:
        "Volume réduit recommandé. Privilégie la mobilité, le yoga ou la marche. Ton corps récupère — c'est normal.",
      color: "hsl(12 85% 55%)",
    };
  }

  const ovulationDay = Math.round(cycleLength / 2);

  // Phase 2 — Folliculaire
  if (dayInCycle <= ovulationDay - 1) {
    return {
      phase: "follicular",
      day: dayInCycle,
      daysRemaining: ovulationDay - dayInCycle,
      label: "Phase folliculaire",
      emoji: "🌒",
      trainingAdaptation:
        "Énergie en hausse. Période idéale pour progresser en force et augmenter les charges.",
      color: "hsl(82 85% 55%)",
    };
  }

  // Phase 3 — Ovulatoire
  if (dayInCycle <= ovulationDay + 1) {
    return {
      phase: "ovulatory",
      day: dayInCycle,
      daysRemaining: 2,
      label: "Phase ovulatoire",
      emoji: "🌕",
      trainingAdaptation:
        "Pic d'énergie. Meilleur moment pour les séances intenses et les records personnels.",
      color: "hsl(42 90% 52%)",
    };
  }

  // Phase 4 — Lutéale
  return {
    phase: "luteal",
    day: dayInCycle,
    daysRemaining: cycleLength - dayInCycle + 1,
    label: "Phase lutéale",
    emoji: "🌖",
    trainingAdaptation:
      "Énergie en baisse progressive. Maintiens ton niveau, favorise la récupération active.",
    color: "hsl(175 60% 42%)",
  };
}

export function useCycleTracking() {
  const [cycleData, setCycleData] = useState<CycleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPhase, setCurrentPhase] = useState<PhaseInfo | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth.user?.id;
      if (!userId) {
        setCycleData(null);
        setCurrentPhase(null);
        return;
      }

      const { data, error } = await supabase
        .from("cycle_tracking")
        .select(
          "cycle_length_days, period_length_days, last_period_start, tracking_enabled, show_in_dashboard, share_with_coach",
        )
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        console.warn("[useCycleTracking] load error:", error.message);
        setCycleData(null);
        setCurrentPhase(null);
        return;
      }

      const next: CycleData | null = data
        ? {
            cycle_length_days: data.cycle_length_days,
            period_length_days: data.period_length_days,
            last_period_start: data.last_period_start,
            tracking_enabled: data.tracking_enabled,
            show_in_dashboard: data.show_in_dashboard,
            share_with_coach: data.share_with_coach,
          }
        : null;

      setCycleData(next);

      if (next?.last_period_start && next.tracking_enabled) {
        setCurrentPhase(
          computeCurrentPhase(
            next.last_period_start,
            next.cycle_length_days,
            next.period_length_days,
          ),
        );
      } else {
        setCurrentPhase(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveCycle = useCallback(
    async (patch: Partial<CycleData>) => {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth.user?.id;
      if (!userId) {
        return { ok: false, error: "not_authenticated" as const };
      }

      const merged: CycleData = { ...DEFAULT_CYCLE, ...(cycleData ?? {}), ...patch };

      const { error } = await supabase
        .from("cycle_tracking")
        .upsert(
          { user_id: userId, ...merged },
          { onConflict: "user_id" },
        );

      if (error) {
        console.warn("[useCycleTracking] save error:", error.message);
        return { ok: false, error: error.message };
      }

      await load();
      return { ok: true as const };
    },
    [cycleData, load],
  );

  return { cycleData, loading, currentPhase, saveCycle, reload: load };
}
